import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  IPublicFilterJob,
  IPublicApiJobFilters,
  JobCitiesResultsType
} from 'core/interface/job';
import useLocales from '../../../hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { getCareerLevelLabels, getSalaryLabels } from 'constants/constants';
import Checkboxes from 'components/shared/Checkbox/Index';
import Autocomplete from 'components/shared/Autocomplete/Index';
import Button from 'components/Button/Index';
import { ProfileService } from 'services/profile/profile';
import DropDown from 'components/Dropdown';
import {
  degreeTypes,
  companySizeChoices
} from 'core/constants/aboutMePopUpConstants';
import { IndustryRedux } from 'core/interface/education';
import { Form, Formik } from 'formik';
import { contractTypes } from 'core/constants/workType';
import { hourlyRateChoices } from 'core/constants/preferenceConstats';

import styles from './index.module.scss';
import { ICandidateLanguage } from 'core/interface/candidate-language';
import { useDebounce } from 'helpers/useDebounce';

const EMPTY_INDUSTRIES: never[] = [];

interface FilterState {
  q: string;
  industries: string;
  locations: {
    id: string | number;
    name: string;
  };
  skills: string[];
  degrees: string;
  compensations: string[];
  work_exps: string[];
  languages: ICandidateLanguage[];
  is_remote: boolean;
  contracts: string[];
  company_sizes: string[];
  [key: string]:
    | string
    | string[]
    | boolean
    | ICandidateLanguage[]
    | { id: string | number; name: string };
}

const PublicJobFilters: FC<IPublicFilterJob> = ({
  applyFilter,
  showMyCompanyFilter = false,
  showOnlyMyCompany = false,
  onShowOnlyMyCompanyChange
}): JSX.Element => {
  const { t } = useLocales();
  const industries = useSelector(
    (selector: IndustryRedux) =>
      selector.industries?.industry ?? EMPTY_INDUSTRIES
  );

  const [resetDropdown, setResetDropdown] = useState<boolean>(false);
  const [locationOptions, setLocationOptions] = useState<
    JobCitiesResultsType[]
  >([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [values, setValues] = useState<FilterState>({
    q: '',
    industries: '',
    locations: {
      id: '',
      name: ''
    },
    skills: [],
    degrees: '',
    compensations: [],
    work_exps: [],
    languages: [],
    is_remote: false,
    contracts: [],
    company_sizes: []
  });

  const [industryQuery, setIndustryQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');

  const debouncedSearchQuery = useDebounce(values.q, 500);
  const debouncedIndustryQuery = useDebounce(industryQuery, 500);
  const debouncedLocationQuery = useDebounce(locationQuery, 500);

  const sanitizeParam = (value?: string | number | null): string => {
    if (value === undefined || value === null) {
      return '';
    }
    const asString = String(value);
    if (!asString || asString === 'undefined' || asString === 'null') {
      return '';
    }
    return asString;
  };

  const toApiParams = (filterValues: FilterState): IPublicApiJobFilters => {
    return {
      q: sanitizeParam(filterValues.q),
      industries: sanitizeParam(filterValues.industries),
      locations: sanitizeParam(filterValues.locations?.id),
      skills: filterValues.skills.join(','),
      compensations: filterValues.compensations.join(','),
      work_exps: filterValues.work_exps.join(','),
      company_sizes: filterValues.company_sizes.join(','),
      contracts: filterValues.contracts.join(','),
      languages: '',
      is_remote: filterValues.is_remote,
      degrees: sanitizeParam(filterValues.degrees)
    };
  };

  const applyCurrentFilters = (filterValues: FilterState): void => {
    applyFilter(toApiParams(filterValues));
  };

  const handleCheckbox = (e: string, key: string): void => {
    const value = [...(values[key] as string[])];
    const index = value.indexOf(e);
    if (index !== -1) {
      value.splice(index, 1);
    } else {
      value.push(e);
    }

    const updatedValues = {
      ...values,
      [key]: value
    };
    setValues(updatedValues);
    applyCurrentFilters(updatedValues);
  };

  // Search industries only when the user types (debounced)
  useEffect(() => {
    if (debouncedIndustryQuery.trim()) {
      ProfileService.getIndustries(debouncedIndustryQuery);
    }
  }, [debouncedIndustryQuery]);

  // Local location options only — ignore Redux cities (filled by job cards / other pages)
  useEffect(() => {
    if (!debouncedLocationQuery.trim()) {
      setLocationOptions([]);
      return;
    }

    let cancelled = false;
    setIsLoadingLocations(true);
    JobService.getLocations({ q: debouncedLocationQuery })
      .then((res) => {
        if (!cancelled) {
          setLocationOptions(res?.results || []);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingLocations(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedLocationQuery]);

  // Debounced search text triggers backend search
  useEffect(() => {
    if (debouncedSearchQuery !== undefined) {
      applyCurrentFilters({ ...values, q: debouncedSearchQuery });
    }
    // Intentionally depend only on debounced search; other filters apply immediately
    // eslint-disable-next-line
  }, [debouncedSearchQuery]);

  const handleLoadMoreCities = () => {
    if (isLoadingLocations || !debouncedLocationQuery.trim()) {
      return;
    }
    setIsLoadingLocations(true);
    JobService.getLocations(
      {
        limit: 10,
        offset: locationOptions.length,
        q: debouncedLocationQuery
      },
      true
    )
      .then((res) => {
        if (res?.results?.length) {
          setLocationOptions((prev) => {
            const existingIds = new Set(prev.map((city) => city.id));
            const next = res.results.filter(
              (city: JobCitiesResultsType) => !existingIds.has(city.id)
            );
            return [...prev, ...next];
          });
        }
      })
      .finally(() => setIsLoadingLocations(false));
  };

  const handleResetFilters = () => {
    const val: FilterState = {
      q: '',
      industries: '',
      locations: {
        id: '',
        name: ''
      },
      skills: [],
      degrees: '',
      compensations: [],
      work_exps: [],
      languages: [],
      is_remote: false,
      contracts: [],
      company_sizes: []
    };

    setValues(val);
    applyCurrentFilters(val);
    setIndustryQuery('');
    setLocationQuery('');
    setLocationOptions([]);
    setResetDropdown(true);
    setTimeout(() => {
      setResetDropdown(true);
    }, 0);
  };

  const handleClearIndustry = () => {
    const updatedValues = {
      ...values,
      industries: ''
    };
    setValues(updatedValues);
    applyCurrentFilters(updatedValues);
  };

  const handleClearDegreeType = () => {
    const updatedValues = {
      ...values,
      degrees: ''
    };
    setValues(updatedValues);
    applyCurrentFilters(updatedValues);
  };

  const handleClearLocation = () => {
    const updatedValues = {
      ...values,
      locations: {
        id: '',
        name: ''
      }
    };
    setValues(updatedValues);
    applyCurrentFilters(updatedValues);
    setLocationQuery('');
    setLocationOptions([]);
  };

  const renderCompensationSection = () => {
    return (
      <div className={styles.filters_checkbox}>
        <h1>{t('Compensations (EUR)')}</h1>

        <h3 className={styles.section_header}>{t('Monthly Salary')}</h3>
        {getSalaryLabels(values.compensations).map((e, i) => (
          <Checkboxes
            checkboxes={e}
            onArrayChange={(e) => {
              if (Array.isArray(e)) {
                handleCheckbox(e[0].value ?? e[0].label, 'compensations');
              }
            }}
            key={`monthly-${i}`}
          />
        ))}

        <h3 className={styles.section_header}>{t('Hourly Rate')}</h3>
        {hourlyRateChoices.map((choice, i) => (
          <Checkboxes
            checkboxes={[
              {
                label: String(choice.to_show),
                isChecked: values.compensations?.includes(String(choice.value)),
                value: String(choice.value)
              }
            ]}
            onArrayChange={(e) => {
              if (Array.isArray(e)) {
                handleCheckbox(e[0].value ?? e[0].label, 'compensations');
              }
            }}
            key={`hourly-${i}`}
          />
        ))}
      </div>
    );
  };

  return (
    <Formik
      className={styles.filters}
      initialValues={{}}
      onSubmit={(e) => console.log(e)}
    >
      <Form className={styles.filters}>
        <div className={styles.filters}>
          <h1>{t('Filter')}</h1>

          {/* Search Input */}
          <div className={styles.filters_search_input}>
            <h1>{t('Search Jobs')}</h1>
            <div className={styles.input_container}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('Search by job title, keyword, etc.')}
                value={values.q}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValues({
                    ...values,
                    q: e.target.value
                  });
                }}
              />
            </div>
          </div>

          {/* 1. Industry */}
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Industry')}</h1>
            <Autocomplete
              fieldName="industry"
              show_arrow={false}
              value={
                industries.find(
                  (industry) =>
                    String(industry?.id) === String(values.industries)
                )?.name ?? ''
              }
              options={
                industries?.map((e) => ({
                  id: e?.id,
                  title: e?.name
                })) || []
              }
              show_location={false}
              placeholder={t('Type to search…')}
              onChange={(e) => {
                const industryId = industries.find(
                  (industry) => industry.name === e
                )?.id;
                if (!industryId) {
                  return;
                }
                const updatedValues = {
                  ...values,
                  industries: String(industryId)
                };
                setValues(updatedValues);
                applyCurrentFilters(updatedValues);
              }}
              onInputChange={(e) => setIndustryQuery(e)}
              values={industries}
              onClear={handleClearIndustry}
            />
          </div>

          {/* 2. Location */}
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('City')}</h1>
            <Autocomplete
              fieldName="location"
              show_arrow={false}
              value={values.locations.name}
              options={
                locationOptions.map((e) => ({
                  id: e?.id,
                  title: e?.display_name
                })) || []
              }
              show_location={true}
              placeholder={t('Type to search…')}
              onChange={(location) => {
                const activeItem = locationOptions.find(
                  (city) => city.display_name === location
                );
                if (!activeItem?.id) {
                  return;
                }

                const updatedValues = {
                  ...values,
                  locations: {
                    id: activeItem.id,
                    name: activeItem.display_name || ''
                  }
                };
                setValues(updatedValues);
                applyCurrentFilters(updatedValues);
              }}
              onInputChange={(e) => setLocationQuery(e)}
              values={locationOptions}
              loadMore={handleLoadMoreCities}
              onClear={handleClearLocation}
            />
          </div>

          {/* 3. Degree Type */}
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Degree Type')}</h1>
            <DropDown
              onReset={resetDropdown}
              defaultValue={values.degrees}
              placeholder={t('Degree type')}
              dropdownClassName={`dashboard__left-dropdown ${styles.filters_dropdown}`}
              items={degreeTypes}
              selectedValue={(e) => {
                const updatedValues = { ...values, degrees: e };
                setValues(updatedValues);
                applyCurrentFilters(updatedValues);
              }}
              onClear={handleClearDegreeType}
            />
          </div>
          <div className={styles.filters_wrapper}>
            {/* 4. Contract Type */}
            <div className={styles.filters_checkbox}>
              <h1>{t('Contract Type')}</h1>
              {contractTypes.map((contract, i) => (
                <Checkboxes
                  key={i}
                  checkboxes={[
                    {
                      label: String(contract.to_show),
                      value: String(contract.value),
                      isChecked: values.contracts.includes(
                        String(contract.value)
                      )
                    }
                  ]}
                  onArrayChange={(e) => {
                    if (
                      Array.isArray(e) &&
                      e[0] &&
                      (e[0].value || e[0].label)
                    ) {
                      const value = e[0].value
                        ? String(e[0].value)
                        : e[0].label;
                      handleCheckbox(value, 'contracts');
                    }
                  }}
                />
              ))}
            </div>

            {/* 5. Compensations */}
            {renderCompensationSection()}

            {/* 6. Career level */}
            <div className={styles.filters_checkbox}>
              <h1>{t('Career level')}</h1>
              {getCareerLevelLabels(values.work_exps).map((e, i) => (
                <Checkboxes
                  checkboxes={e}
                  onArrayChange={(e) => {
                    if (Array.isArray(e)) {
                      handleCheckbox(e[0].value ?? e[0].label, 'work_exps');
                    }
                  }}
                  key={i}
                />
              ))}
            </div>

            {/* 7. Company Size */}
            <div className={styles.filters_checkbox}>
              <h1>{t('Company Size')}</h1>
              {companySizeChoices.map((companySize, i) => (
                <Checkboxes
                  key={i}
                  checkboxes={[
                    {
                      label: String(companySize.to_show),
                      value: String(companySize.value),
                      isChecked:
                        Array.isArray(values.company_sizes) &&
                        values.company_sizes.includes(String(companySize.value))
                    }
                  ]}
                  onArrayChange={(e) => {
                    if (
                      Array.isArray(e) &&
                      e[0] &&
                      (e[0].value || e[0].label)
                    ) {
                      const value = e[0].value
                        ? String(e[0].value)
                        : e[0].label;
                      handleCheckbox(value, 'company_sizes');
                    }
                  }}
                />
              ))}
            </div>
          </div>

          {/* 8. Is Remote */}
          <div className={styles.filters_checkbox}>
            <h1>{t('Is a remote job')}</h1>
            <Checkboxes
              onArrayChange={(e) => {
                if (Array.isArray(e)) {
                  const updatedValues = {
                    ...values,
                    is_remote: e[0].isChecked || false
                  };
                  setValues(updatedValues);
                  applyCurrentFilters(updatedValues);
                }
              }}
              checkboxes={[
                {
                  label: t('Remote'),
                  isChecked: values?.is_remote
                }
              ]}
            />
          </div>

          {showMyCompanyFilter && (
            <div className={styles.filters_checkbox}>
              <h1>{t('My company')}</h1>
              <Checkboxes
                onArrayChange={(e) => {
                  if (Array.isArray(e)) {
                    onShowOnlyMyCompanyChange?.(e[0].isChecked || false);
                  }
                }}
                checkboxes={[
                  {
                    label: t('My company jobs only'),
                    isChecked: showOnlyMyCompany
                  }
                ]}
              />
            </div>
          )}

          <div className={styles.filters_reset_button}>
            <Button
              t={t}
              className={`navbar__left-btn ${styles.filters_reset} ${styles.filters_buttons}`}
              title={t('Reset Filters')}
              onClick={handleResetFilters}
            />
          </div>
        </div>
      </Form>
    </Formik>
  );
};

export default PublicJobFilters;
